<template>
  <div
    class="py-10 px-4 md:py-6 md:px-24 flex items-center justify-center md:rounded-3xl bg-brand-bg-main blur-bg h-full w-full md:h-auto md:max-h-[90vh] md:max-w-desktop my-6 overflow-y-auto"
  >
    <form
      id="payment-form"
      class="flex-1 w-full h-full flex items-stretch justify-start flex-col gap-5"
      @submit.prevent="onSubmit"
    >
      <WithSkeleton :loading="!hasInitialized">
        <template #loading>
          <div class="flex items-center justify-start gap-3">
            <Skeleton
              width="32px"
              height="32px"
              border-radius="99999px"
            ></Skeleton>
            <Skeleton width="32px" height="32px" border-radius="8px"></Skeleton>
          </div>
        </template>

        <div class="flex items-center justify-between gap-3">
          <img
            class="h-8 w-8"
            :src="channelStore.channel?.logo || ''"
            :alt="
              channelStore.channel?.name
                ? `${channelStore.channel.name} logo`
                : 'Channel logo'
            "
          />

          <button
            class="p-2 w-auto h-auto bg-brand-bgdivider-20 text-basics-primary border-0 rounded-full"
            type="button"
            @click="emit('close')"
          >
            <Icon icon="mdi:close" class="text-2xl" />
          </button>
        </div>
      </WithSkeleton>

      <WithSkeleton :loading="!hasInitialized">
        <template #loading>
          <Skeleton width="100%" height="50px" border-radiuse="8px" />
        </template>

        <div class="flex flex-col items-start justify-start">
          <h3 v-if="title" class="text-basics-secondary-80">
            {{ title }}
          </h3>
          <div v-if="effectivePrice" class="flex items-end gap-2">
            <h1 class="text-basics-primary font-serif text-4xl">
              {{ effectivePrice }}
            </h1>
          </div>
        </div>
      </WithSkeleton>

      <div v-show="hasInitialized" id="payment-element">
        <!-- Elements will create form elements here -->
      </div>

      <!-- Pre-contractual information for EU compliance -->
      <div
        v-if="hasInitialized && subscriptionId"
        class="flex flex-col gap-1 text-xs text-basics-secondary-80"
      >
        <p v-if="isSubscription">
          {{
            $t("checkout.autoRenewal", {
              interval: billingInterval,
              price: priceLabel || price || "",
            })
          }}
        </p>
        <p v-if="isSubscription">
          {{ $t("checkout.cancellation") }}
        </p>
        <p>
          {{ $t("checkout.termsAgreement") }}
          <RouterLink
            :to="{ name: RouteNames.TermsOfService }"
            class="underline"
            target="_blank"
          >
            {{ $t("checkout.termsLink") }}
            <span class="sr-only">{{ $t("checkout.opensInNewTab") }}</span>
          </RouterLink>
        </p>
      </div>

      <WithSkeleton :loading="!hasInitialized">
        <template #loading>
          <Skeleton width="100%" height="50px" border-radiuse="8px" />
        </template>
        <ButtonV2
          class="w-full rounded-lg mt-2"
          size="lg"
          variant="primary"
          type="submit"
          :disabled="loading"
        >
          <span v-if="loading" class="loading loading-spinner"></span>
          <template v-else>{{ effectiveSubmitLabel }}</template>
        </ButtonV2>
      </WithSkeleton>

      <div id="error-message">
        <!-- Display error message to your customers here -->
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { graphql as gql } from "@/generated";

export const InitiatePurchaseMutationDocument = gql(/* GraphQL */ `
  mutation InitiatePurchase($product_id: String!) {
    initiatePurchase(product_id: $product_id) {
      ... on InitiatePurchaseResult {
        purchase_id
        subscription_id
        client_secret
        publishable_key
        price
      }
      ... on UnauthenticatedError {
        error
      }
      ... on NoChannelError {
        error
      }
      ... on ProductNotFoundError {
        error
        product_id
      }
      ... on AlreadyHasActiveSubscriptionError {
        error
        channel_id
      }
      ... on StripeConfigNotFoundError {
        error
        channel_id
      }
      ... on NoPaymentMethodError {
        error
      }
      ... on NoAutomaticChargeError {
        error
        client_secret
        payment_intent_id
        publishable_key
        payment_method_id
        amount_due
        currency
        proration_amount_display
      }
      ... on StripeError {
        error
        message
      }
    }
  }
`);
</script>

<script setup lang="ts">
import { ref, toValue, watch, computed } from "vue";
import { useMutation } from "@vue/apollo-composable";
import { useToastStore } from "@/stores/toast.store";
import { useStripe } from "@/composables/use-stripe";
import { useAwaitAction } from "@/composables/use-await-action";
import { useChannelStore } from "@/stores/channel.store";
import { Icon } from "@iconify/vue";
import Skeleton from "@/components/ui/layout/skeleton.vue";
import WithSkeleton from "@/components/ui/layout/with-skeleton.vue";
import ButtonV2 from "@/components/ui/button/button-v2.vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";
import { RouteNames } from "@/router/app.router";

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    productId: string;
    title?: string;
    priceLabel?: string;
    submitLabel?: string;
    errorMessage?: string;
    billingInterval?: string;
  }>(),
  {
    title: "",
    priceLabel: "",
    submitLabel: "",
    errorMessage: "",
    billingInterval: "month",
  },
);

const emit = defineEmits<{
  close: [];
  success: [paymentIntentId: string];
}>();

const toastStore = useToastStore();
const channelStore = useChannelStore();

const clientSecret = ref<string | null>(null);
const subscriptionId = ref<string | null>(null);
const purchaseId = ref<string | null>(null);
const price = ref<string | null>(null);
const { confirmPaymentRef, hasInitialized, init } = useStripe();

const { mutate: initiatePurchase } = useMutation(
  InitiatePurchaseMutationDocument,
);

watch(
  () => props.productId,
  async (newProductId) => {
    if (clientSecret.value !== null) return;

    if (!newProductId) return;

    const result = await initiatePurchase({
      product_id: newProductId,
    });

    const response = result?.data?.initiatePurchase;

    if (response?.__typename === "InitiatePurchaseResult") {
      purchaseId.value = response.purchase_id;
      subscriptionId.value = response.subscription_id ?? null;
      clientSecret.value = response.client_secret ?? null;
      price.value = response.price ?? null;

      const publishableKey = response.publishable_key;

      if (clientSecret.value && publishableKey) {
        await init(publishableKey, clientSecret.value, "#payment-element");
      }
      return;
    }

    // Existing subscriber whose saved card cannot be auto-charged (no default
    // method, or SCA required) is routed here to pay the proration off-session.
    // Bind the card form to the proration PaymentIntent's client_secret instead
    // of dead-ending on a generic error, so they can enter a card and confirm.
    if (
      response?.__typename === "NoAutomaticChargeError" &&
      response.client_secret &&
      response.publishable_key
    ) {
      clientSecret.value = response.client_secret;
      price.value = response.proration_amount_display ?? price.value;
      await init(
        response.publishable_key,
        response.client_secret,
        "#payment-element",
      );
      return;
    }

    if (response && "error" in response) {
      console.error(
        "[ProductCheckout] Error initiating purchase:",
        response.error,
      );
    }

    toastStore.addToast({
      message: props.errorMessage || t("ui.payment.subscriptionPurchaseError"),
      type: "error",
    });
  },
  { immediate: true },
);

const { handler: onSubmit, loading } = useAwaitAction(async () => {
  const confirmPayment = toValue(confirmPaymentRef);
  const paymentIntentId = await confirmPayment();

  if (!paymentIntentId) {
    emit("close");
    return;
  }

  emit("success", paymentIntentId);
});

// Effective price: prefer prop (parent-provided), fallback to mutation response
const effectivePrice = computed(() => props.priceLabel || price.value || "");

// Computed properties for compliance
const isSubscription = computed(() => subscriptionId.value !== null);

const effectiveSubmitLabel = computed(() => {
  // If parent provided submitLabel, use it unchanged
  if (props.submitLabel) {
    return props.submitLabel;
  }

  // Free tier case
  // Check for free products — formatMoney returns locale-formatted zero amounts
  // e.g. "$0.00", "€0.00", "¥0". Match any zero-amount formatted string.
  const isFreePrice =
    !price.value ||
    price.value === "0" ||
    /^[^1-9]*0([.,]0+)?[^1-9]*$/.test(price.value);
  if (isFreePrice) {
    return t("checkout.subscribeForFree");
  }

  // Subscription vs one-time purchase
  if (isSubscription.value) {
    return t("checkout.subscribeAndPay", {
      price: effectivePrice.value,
      interval: props.billingInterval,
    });
  }

  return t("checkout.payNow", {
    price: effectivePrice.value,
  });
});

defineExpose({
  price,
  purchaseId,
  subscriptionId,
});
</script>
